/**
 * Digital Downloads — Redesigned Layout
 * Ticket: DLT-16
 *
 * Runs only on /apps/digital-downloads/* pages.
 *
 * Strategy:
 *   1. Reads all data from the DDA app's existing DOM.
 *   2. Builds a new two-column layout (Figma design).
 *   3. Inserts breadcrumbs + new layout above <main>.
 *   4. Adds `ddl-ready` to <body> → CSS hides original markup.
 *   5. Download buttons proxy-click the original hidden DDA
 *      links so the app's signed-URL logic is fully preserved.
 *
 * Depends on: app/styles/templates/digital-downloads.scss
 */

(function () {
  "use strict";

  // ── Guard: only run on digital downloads pages ─────────────
  if (window.location.pathname.indexOf("/apps/digital-downloads/") === -1) {
    return;
  }

  // ── SVG icon strings ────────────────────────────────────────

  // Lucide "chevron-right" (16×16) — breadcrumb separator, matches Figma ChevronLeft rotate-180
  var ICON_CHEVRON = [
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"',
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round"',
    ' width="16" height="16" aria-hidden="true">',
    '<polyline points="9 18 15 12 9 6"/>',
    "</svg>",
  ].join("");

  // Lucide "file-text" — used for PDF
  var ICON_PDF = [
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"',
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round"',
    ' aria-hidden="true">',
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>',
    '<polyline points="14 2 14 8 20 8"/>',
    '<line x1="16" y1="13" x2="8" y2="13"/>',
    '<line x1="16" y1="17" x2="8" y2="17"/>',
    '<polyline points="10 9 9 9 8 9"/>',
    "</svg>",
  ].join("");

  // Lucide "table" — used for spreadsheets
  var ICON_SPREADSHEET = [
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"',
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round"',
    ' aria-hidden="true">',
    '<rect x="3" y="3" width="18" height="18" rx="2"/>',
    '<line x1="3" y1="9" x2="21" y2="9"/>',
    '<line x1="3" y1="15" x2="21" y2="15"/>',
    '<line x1="12" y1="3" x2="12" y2="21"/>',
    "</svg>",
  ].join("");

  // Lucide "download" — used as the right-hand action icon
  var ICON_DOWNLOAD = [
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"',
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round"',
    ' aria-hidden="true">',
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>',
    '<polyline points="7 10 12 15 17 10"/>',
    '<line x1="12" y1="15" x2="12" y2="3"/>',
    "</svg>",
  ].join("");

  // ── Helpers ─────────────────────────────────────────────────

  /**
   * Determines file type from the DDA meta label or filename extension.
   * Returns 'pdf' | 'spreadsheet'
   */
  function getFileType(filename, metaLabel) {
    var label = (metaLabel || "").trim().toLowerCase();
    if (label === "pdf") return "pdf";
    if (label === "xlsx" || label === "xls" || label === "csv")
      return "spreadsheet";

    var ext = (filename || "").split(".").pop().toLowerCase();
    if (ext === "pdf") return "pdf";
    if (ext === "xlsx" || ext === "xls" || ext === "csv") return "spreadsheet";

    return "pdf"; // safe default
  }

  /**
   * Formats the DDA date string (e.g. "16 December, 2024 16:04")
   * into DD/MM/YYYY as shown in Figma.
   */
  function formatDate(raw) {
    if (!raw) return "";
    try {
      // Remove the comma so Date() can parse it reliably
      var d = new Date(raw.replace(",", ""));
      if (!isNaN(d.getTime())) {
        var dd = String(d.getDate()).padStart(2, "0");
        var mm = String(d.getMonth() + 1).padStart(2, "0");
        return dd + "/" + mm + "/" + d.getFullYear();
      }
    } catch (e) {
      /* fall through */
    }
    return raw;
  }

  /** Tiny element factory. */
  function el(tag, cls, html) {
    var elem = document.createElement(tag);
    if (cls) elem.className = cls;
    if (html !== undefined) elem.innerHTML = html;
    return elem;
  }

  // ── Breadcrumbs ─────────────────────────────────────────────

  /**
   * Builds the breadcrumb nav:
   *   Home  ›  Order #XXXXX  ›  Download
   *
   * The Shopify order ID is the last segment of the current URL
   * path, e.g. /apps/digital-downloads/orders/6165322268984.
   */
  function buildBreadcrumbs(orderNumber) {
    var segments = window.location.pathname.split("/");
    var orderId = segments[segments.length - 1];

    var nav = el("nav", "ddl-breadcrumbs");
    nav.setAttribute("aria-label", "Breadcrumb");
    nav.innerHTML =
      '<a href="/">Home</a>' +
      '<span class="ddl-breadcrumbs__sep" aria-hidden="true">' +
      ICON_CHEVRON +
      "</span>" +
      '<a href="/account/orders/' +
      orderId +
      '">Order ' +
      orderNumber +
      "</a>" +
      '<span class="ddl-breadcrumbs__sep" aria-hidden="true">' +
      ICON_CHEVRON +
      "</span>" +
      '<span class="ddl-breadcrumbs__current" aria-current="page">Download</span>';

    return nav;
  }

  // ── Download button ──────────────────────────────────────────

  /**
   * Builds a single styled download button for one DDA asset.
   *
   * The button is a <div role="button"> that proxy-clicks the
   * original hidden DDA <a> element. This preserves the app's
   * signed-URL / expiry logic without touching the href directly.
   */
  function buildButton(asset) {
    // -- Read data from original DDA elements --
    var filenameEl = asset.querySelector(".dda-order__asset-filename span");
    var filename = filenameEl ? filenameEl.textContent.trim() : "Download";

    var metaEl = asset.querySelector(".dda-order__asset-meta_label");
    var metaText = metaEl ? metaEl.textContent.trim() : "";

    var originalLink = asset.querySelector(".dda-order__asset-link a");

    // -- Determine type for colour coding --
    var type = getFileType(filename, metaText);
    var icon = type === "spreadsheet" ? ICON_SPREADSHEET : ICON_PDF;

    // -- Build button element --
    var btn = el("div", "ddl-btn ddl-btn--" + type);
    btn.setAttribute("role", "button");
    btn.setAttribute("tabindex", "0");
    btn.setAttribute("aria-label", "Download " + filename);
    btn.innerHTML =
      '<span class="ddl-btn__icon">' +
      icon +
      "</span>" +
      '<span class="ddl-btn__label">' +
      filename +
      "</span>" +
      '<span class="ddl-btn__download">' +
      ICON_DOWNLOAD +
      "</span>";

    // -- Proxy click → original DDA link --
    function triggerDownload() {
      if (originalLink) {
        originalLink.click();
      }
    }

    btn.addEventListener("click", triggerDownload);
    btn.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        triggerDownload();
      }
    });

    return btn;
  }

  // ── Main init ────────────────────────────────────────────────

  function init() {
    var dda = document.querySelector(".dda-orders");
    if (!dda) return; // app hasn't rendered yet — shouldn't happen on DOMContentLoaded

    var order = document.querySelector(".dda-order");
    if (!order) return;

    // ── Extract order-level metadata ───────────────────────────

    var orderNumberEl = order.querySelector(".dda-order__number");
    var orderNumber = orderNumberEl ? orderNumberEl.textContent.trim() : "";

    var orderDateEl = order.querySelector(".dda-order__date time");
    var orderDateRaw = orderDateEl ? orderDateEl.textContent.trim() : "";
    var orderDate = formatDate(orderDateRaw);

    // ── Extract item-level data (use first item) ───────────────
    // Most orders have a single product; multi-product orders
    // still share one cover image + one publication name here.

    var firstItem = order.querySelector(".dda-order__item");
    if (!firstItem) return;

    var coverImgEl = firstItem.querySelector(".dda-order__item-image");
    var coverSrc = coverImgEl ? coverImgEl.getAttribute("src") : "";
    var coverAlt = coverImgEl ? coverImgEl.getAttribute("alt") || "" : "";

    var pubNameEl = firstItem.querySelector(".dda-order__item-name");
    var pubName = pubNameEl ? pubNameEl.textContent.trim() : "";

    // Gather every downloadable asset across all items
    var allAssets = order.querySelectorAll(".dda-order__asset");
    if (!allAssets.length) return;

    // ── Breadcrumbs ────────────────────────────────────────────

    var breadcrumbs = buildBreadcrumbs(orderNumber);

    // ── Page wrapper ───────────────────────────────────────────

    var page = el("div", "ddl-page");
    var layout = el("div", "ddl-layout");

    // ── Left column: cover image ───────────────────────────────

    var colImage = el("div", "ddl-col-image");

    if (coverSrc) {
      var img = document.createElement("img");
      img.src = coverSrc;
      img.alt = coverAlt;
      img.loading = "lazy";
      colImage.appendChild(img);
    }

    // ── Right column: details card ─────────────────────────────

    var colDetails = el("div", "ddl-col-details");
    var card = el("div", "ddl-card");

    // Card top section: "Your Download" + order meta
    var cardTop = el("div", "ddl-card__top");
    var heading = el("h2", "ddl-card__heading");
    heading.textContent = "Your Download";

    var metaList = el("div", "ddl-meta-list");

    [
      { label: "Order number", value: orderNumber },
      { label: "Order date", value: orderDate },
      { label: "Publication", value: pubName },
    ].forEach(function (row) {
      if (!row.value) return;
      var r = el("div", "ddl-meta-row");
      r.innerHTML =
        '<span class="ddl-meta-label">' +
        row.label +
        "</span>" +
        '<span class="ddl-meta-value">' +
        row.value +
        "</span>";
      metaList.appendChild(r);
    });

    cardTop.appendChild(heading);
    cardTop.appendChild(metaList);

    // Download buttons section
    var buttonsWrapper = el("div", "ddl-buttons");
    allAssets.forEach(function (asset) {
      buttonsWrapper.appendChild(buildButton(asset));
    });

    // Assemble card
    card.appendChild(cardTop);
    card.appendChild(buttonsWrapper);
    colDetails.appendChild(card);

    // Assemble layout
    layout.appendChild(colImage);
    layout.appendChild(colDetails);
    page.appendChild(layout);

    // ── Inject into page ───────────────────────────────────────
    // Insert breadcrumbs + new layout immediately before <main>
    // so the site header renders above and footer below as normal.

    var mainEl = document.querySelector("main");
    if (mainEl && mainEl.parentNode) {
      mainEl.parentNode.insertBefore(breadcrumbs, mainEl);
      mainEl.parentNode.insertBefore(page, mainEl);
    }

    // Adding this class triggers the CSS to hide the original DDA markup
    document.body.classList.add("ddl-ready");
  }

  // ── Boot ─────────────────────────────────────────────────────
  // The DDA app renders its HTML synchronously, so DOMContentLoaded
  // is early enough. If the document is already interactive/complete
  // (e.g. script loaded with defer), run immediately.

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
